// useScopedQueryKey — helper pra prefixar queryKeys do React Query com
// o scope atual (provider+profile). Garante isolamento automático sem
// caller precisar lembrar de incluir provider/profile na key.
//
// Política:
// - useScopedQueryKey: dados scopados por (provider, perfil). Maioria das
//   queries do app — listas, info de filme/série, EPG, favoritos, etc.
// - useProviderQueryKey: scope só por provider (perfis compartilham).
//   Catálogo bruto, channels, listas de categorias do provider.
// - Sem helper / queryKey "cru": dados verdadeiramente globais entre
//   providers (TMDB minimal-batch, configs do device).
//
// Implementação: hook reativo a `useSessionStore` — quando muda provider
// ou perfil, queryKey muda, RQ trata como query nova (e provider-isolation
// já limpou o cache antigo, então não há vazamento).

import { useSessionStore } from '../store'

const PROFILE_FALLBACK = '_main'

/**
 * Retorna parte do scope do **provider** atual. `null` se logged out.
 * Forma: `p:CODE` ou `d:LIST`.
 */
function useProviderPart(): string | null {
  return useSessionStore((s) => {
    if (s.providerCode) return `p:${s.providerCode}`
    if (s.direct) return `d:${s.direct.listName}`
    return null
  })
}

/**
 * Parte do scope do **perfil** atual. Sempre retorna algo (default `_main`).
 * Forma: `pf:ID`.
 */
function useProfilePart(): string {
  return useSessionStore((s) => `pf:${s.activeProfileId ?? PROFILE_FALLBACK}`)
}

/**
 * Hook que produz queryKey scopada por (provider, perfil).
 *
 * Uso:
 *   const queryKey = useScopedQueryKey(['series-info', id])
 *   useQuery({ queryKey, queryFn: ... })
 *
 * Resultado: `['scope', 'p:CODE::pf:ID', 'series-info', id]`
 *
 * Se logged out, queryKey vira `['scope', 'logged-out', ...]` — safe pq
 * RQ não dispara queries logged out (caller usa `enabled: !!id`).
 */
export function useScopedQueryKey(parts: readonly unknown[]): readonly unknown[] {
  const provider = useProviderPart()
  const profile = useProfilePart()
  const scope = provider ? `${provider}::${profile}` : 'logged-out'
  return ['scope', scope, ...parts]
}

/**
 * Hook que produz queryKey scopada **só por provider** (não inclui perfil).
 * Pra dados que são iguais entre perfis: catálogo bruto, channels, EPG,
 * listas de categorias, snapshot.
 *
 * Resultado: `['provider', 'p:CODE', ...parts]`
 */
export function useProviderQueryKey(parts: readonly unknown[]): readonly unknown[] {
  const provider = useProviderPart()
  const scope = provider ?? 'logged-out'
  return ['provider', scope, ...parts]
}

/**
 * Hook que retorna apenas a string do scope provider+profile. Útil em
 * `useQueries` (que monta keys dentro de .map() — não dá pra chamar
 * outro hook lá). Caller compõe a key:
 *
 *   const scope = useScopeKeyString()
 *   useQueries({ queries: ids.map((id) => ({ queryKey: ['scope', scope, 'info', id], ... })) })
 */
export function useScopeKeyString(): string {
  const provider = useProviderPart()
  const profile = useProfilePart()
  return provider ? `${provider}::${profile}` : 'logged-out'
}

/**
 * Hook que retorna apenas a string do scope só de provider. Mesmo uso
 * em `useQueries` quando dado é compartilhado entre perfis.
 */
export function useProviderScopeString(): string {
  const provider = useProviderPart()
  return provider ?? 'logged-out'
}

/** Helper puro (não-hook) pra montar queryKey provider-scoped dentro de
 *  `useQueries({ queries: items.map(...) })` onde não dá pra chamar hooks.
 *  Caller obtém `scope` via useProviderScopeString() fora do .map().
 *  Garante consistência de prefixo — evita typo manual em ['provider', ...]. */
export function composeProviderKey(
  scope: string,
  ...parts: readonly unknown[]
): readonly unknown[] {
  return ['provider', scope, ...parts]
}

/** Análogo a composeProviderKey pra scope completo (provider+profile). */
export function composeScopedKey(
  scope: string,
  ...parts: readonly unknown[]
): readonly unknown[] {
  return ['scope', scope, ...parts]
}
